import { SshMessageWriter } from '../protocol/message-writer.js';

/**
 * Ephemeral key pair for X25519 key exchange.
 */
export type KexKeyPair = {
	publicKey: Uint8Array;
	privateKey: CryptoKey;
};

/**
 * Result of a completed key exchange.
 */
export type KexResult = {
	/** The shared secret K (as bigint for key derivation) */
	sharedSecret: bigint;
	/** The exchange hash H */
	exchangeHash: Uint8Array;
};

/**
 * Generate an ephemeral X25519 key pair for curve25519-sha256 key exchange.
 */
export async function generateKexKeyPair(): Promise<KexKeyPair> {
	const keyPair = (await crypto.subtle.generateKey(
		'X25519',
		true, // extractable (we need the raw public key)
		['deriveBits'],
	)) as { publicKey: CryptoKey; privateKey: CryptoKey };

	const publicKeyRaw = await crypto.subtle.exportKey('raw', keyPair.publicKey);

	return {
		publicKey: new Uint8Array(publicKeyRaw),
		privateKey: keyPair.privateKey,
	};
}

/**
 * Compute the shared secret from our private key and the server's public key.
 * Returns the raw 32-byte shared secret.
 */
export async function computeSharedSecret(
	privateKey: CryptoKey,
	serverPublicKeyRaw: Uint8Array,
): Promise<Uint8Array> {
	// import the server's raw public key
	const serverPublicKey = await crypto.subtle.importKey(
		'raw',
		new Uint8Array(serverPublicKeyRaw),
		'X25519',
		false,
		[],
	);

	const sharedBits = await crypto.subtle.deriveBits(
		{ name: 'X25519', public: serverPublicKey },
		privateKey,
		256, // 32 bytes
	);

	return new Uint8Array(sharedBits);
}

/**
 * Encode a raw byte array as an SSH mpint bigint.
 * The raw bytes are big-endian unsigned.
 */
export function rawBytesToMpint(raw: Uint8Array): bigint {
	let value = 0n;
	for (const byte of raw) {
		value = (value << 8n) | BigInt(byte);
	}
	return value;
}

/**
 * Compute the exchange hash H for curve25519-sha256.
 *
 * H = SHA-256(
 *   string  V_C    client version string (without CR LF)
 *   string  V_S    server version string (without CR LF)
 *   string  I_C    client KEXINIT payload
 *   string  I_S    server KEXINIT payload
 *   string  K_S    server's host key blob
 *   string  e      client's ephemeral public key (raw 32 bytes)
 *   string  f      server's ephemeral public key (raw 32 bytes)
 *   mpint   K      shared secret
 * )
 */
export async function computeExchangeHash(params: {
	clientVersion: string;
	serverVersion: string;
	clientKexInit: Uint8Array;
	serverKexInit: Uint8Array;
	serverHostKey: Uint8Array;
	clientPublicKey: Uint8Array;
	serverPublicKey: Uint8Array;
	sharedSecret: bigint;
}): Promise<Uint8Array> {
	const w = new SshMessageWriter();
	w.writeString(params.clientVersion);
	w.writeString(params.serverVersion);
	w.writeString(params.clientKexInit);
	w.writeString(params.serverKexInit);
	w.writeString(params.serverHostKey);
	w.writeString(params.clientPublicKey);
	w.writeString(params.serverPublicKey);
	w.writeMpint(params.sharedSecret);

	const payload = w.toUint8Array();
	const hashBuf = await crypto.subtle.digest('SHA-256', new Uint8Array(payload));
	return new Uint8Array(hashBuf);
}
